import {
  ApiUserSummary,
  UpdateUserRolePayload,
  UserListItem,
  UsersStore,
} from "@/users/types/users";
import { defineStore } from "pinia";
import { useSnackBarStore } from "@/shared/stores/snackbar";
import axios from "@/app/plugins/axios";
import { CreateUser, InviteUser } from "@/auth/types/register";
import i18n from "@/app/plugins/i18n";

const getUserErrorMessage = (error: unknown): string => {
  const apiError = error as {
    data?: { error?: string; message?: string } | string;
    message?: string;
  } | null;
  const errorCode =
    apiError?.data && typeof apiError.data === "object" ? apiError.data.error : undefined;
  if (errorCode) {
    return i18n.global.t(`page.users.error.${errorCode}`).toString();
  }

  const message =
    (apiError?.data && typeof apiError.data === "object"
      ? apiError.data.message
      : apiError?.data) ||
    apiError?.message ||
    "Something went wrong.";

  return String(message);
};

export const useUserStore = defineStore("user", {
  state: (): UsersStore => ({
    users: [],
    loading: false,
  }),

  getters: {
    getUserEmail:
      (state) =>
      (id: string): string | null => {
        const user = state.users?.find((user) => user.id === id);
        return user ? user.email : null;
      },

    getUserRole:
      (state) =>
      (id: string): string | null => {
        const user = state.users?.find((user) => user.id === id);
        return user ? user.role : null;
      },

    getUserById:
      (state) =>
      (id: string): UsersStore["users"][0] | undefined => {
        return state.users?.find((user) => user.id === id);
      },
  },

  actions: {
    async fetchUsers(): Promise<void> {
      this.loading = true;
      try {
        const response = await axios.get("/user");
        const users = (response.data.users ?? []) as ApiUserSummary[];
        const invitedUsers = (response.data.invitedUsers ?? []) as ApiUserSummary[];

        // Combine and map users and invitedUsers
        const newUsers: UserListItem[] = users.concat(invitedUsers).map((user) => {
          return {
            id: user.uid ?? "",
            name: user.displayName ? user.displayName : user.email,
            email: user.email,
            role: user.role,
            password: "",
            assignedLocationIds: user.assignedLocationIds ?? null,
            invited: !user.uid,
            inactive: user.inactive,
            mfaEnrolled: user.mfaEnrolled === true,
          };
        });

        this.users = newUsers;
      } catch (error) {
        useSnackBarStore().toastError({
          message: getUserErrorMessage(error),
        });
      } finally {
        this.loading = false;
      }
    },

    async retrieveUser(userId: string): Promise<CreateUser> {
      try {
        const response = await axios.get(`/user/${userId}`);
        return {
          email: response.data.email,
          displayName: response.data.displayName,
          role: response.data.role,
          assignedLocationIds: response.data.assignedLocationIds ?? null,
        };
      } catch (error) {
        useSnackBarStore().toastError({
          message: getUserErrorMessage(error),
        });
        return { email: "", displayName: "", role: "", assignedLocationIds: null };
      }
    },
    async inviteUser(data: InviteUser): Promise<void> {
      const { emails, role, displayName } = data;
      const payload = {
        emails,
        role: role.toLowerCase(),
        displayName,
        ...(Object.prototype.hasOwnProperty.call(data, "assignedLocationIds")
          ? { assignedLocationIds: data.assignedLocationIds ?? null }
          : {}),
      };
      try {
        await axios.post("/user", payload);
        await this.fetchUsers();
        useSnackBarStore().toastSuccess({
          message: i18n.global.t("page.users.invite.add"),
        });
      } catch (error) {
        useSnackBarStore().toastError({
          message: getUserErrorMessage(error),
        });
      }
    },

    async reinviteUser(email: string): Promise<void> {
      try {
        await axios.post("/user/reinvite", { email });
        useSnackBarStore().toastSuccess({
          message: i18n.global.t("page.users.invite.reinvite"),
        });
      } catch (error) {
        useSnackBarStore().toastError({
          message: getUserErrorMessage(error),
        });
      }
    },

    async updateUser(userId: string, payload: UpdateUserRolePayload): Promise<void> {
      try {
        const response = await axios.patch(`/user/${userId}`, payload);
        const newUser = response.data as UserListItem;
        const index = this.users.findIndex((u) => u.id === newUser.id);
        if (index !== -1) {
          this.users.splice(index, 1, newUser);
        }
      } catch (error) {
        useSnackBarStore().toastError({
          message: getUserErrorMessage(error),
        });
      }
    },

    async deleteUser(data: { uid?: string; email?: string }): Promise<void> {
      try {
        const { uid, email } = data;
        const payload: { uid?: string; email?: string } = { uid, email };

        await axios.post("/user/delete", payload);
        useSnackBarStore().toastSuccess({
          message: i18n.global.t("common.deleteMsg", [i18n.global.t("common.entity.user")]),
        });
        await this.fetchUsers();
      } catch (error) {
        useSnackBarStore().toastError({
          message: getUserErrorMessage(error),
        });
      }
    },

    async deactivateUser(userId: string): Promise<void> {
      try {
        await axios.post(`/user/deactivate/${userId}`);
        useSnackBarStore().toastSuccess({
          message: i18n.global.t("common.deactivateMsg", [i18n.global.t("common.entity.user")]),
        });
        await this.fetchUsers();
      } catch (error) {
        useSnackBarStore().toastError({
          message: getUserErrorMessage(error),
        });
      }
    },

    async activateUser(userId: string): Promise<void> {
      try {
        await axios.post(`/user/active/${userId}`);
        useSnackBarStore().toastSuccess({
          message: i18n.global.t("common.activateMsg", [i18n.global.t("common.entity.user")]),
        });
        await this.fetchUsers();
      } catch (error) {
        useSnackBarStore().toastError({
          message: getUserErrorMessage(error),
        });
      }
    },

    addUser({ state }: { state: UsersStore }, user: Omit<UsersStore["users"][0], "id">): void {
      const id = state.users.length + 1;
      const newUser = { ...user, id: String(id) };
      state.users.push(newUser);
    },
  },
});
